import { MessageSquareWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Tabs,
  type TabItem,
} from '@/components/ui';
import { useRegisterKinds } from '@/features/registers/api';
import { PageContainer } from '@/layouts/PageContainer';
import { COMPLAINT_KIND } from '@/pages/ops/complaint-format';
import { RegisterCard } from '@/pages/ops/OperationalRecords';
import styles from './ServiceRatings.module.css';
import { ServiceRatings } from './ServiceRatings';

type ComplaintsTab = 'complaints' | 'ratings';

const TABS: TabItem<ComplaintsTab>[] = [
  { value: 'complaints', label: 'Complaints' },
  { value: 'ratings', label: 'Service ratings' },
];

/**
 * Complaints (RL-1137): the complaint register, including complaints made in
 * the patient app (RL-954: case number, owner, deadlines), and the service
 * ratings patients give after a visit (same permission, `complaints:read`).
 * The tab is kept in `?tab=`.
 */
export function ComplaintsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((i) => i.value === params.get('tab'))?.value;
  const current = tab ?? 'complaints';
  return (
    <PageContainer>
      <div className={styles.stack}>
        <Tabs
          variant="bar"
          label="Complaints"
          value={current}
          onChange={(next) =>
            setParams(
              (p) => {
                p.set('tab', next);
                return p;
              },
              { replace: true },
            )
          }
          items={TABS}
        />
        {current === 'complaints' ? <ComplaintRegister /> : <ServiceRatings />}
      </div>
    </PageContainer>
  );
}

function ComplaintRegister() {
  const { t } = useTranslation();
  const catalog = useRegisterKinds();
  const kind = catalog.data?.find((k) => k.kind === COMPLAINT_KIND);
  if (catalog.isLoading) return <LoadingState />;
  if (catalog.error)
    return (
      <ErrorState
        error={catalog.error}
        onRetry={() => void catalog.refetch()}
      />
    );
  if (!kind)
    return (
      <Card title="Complaints">
        <EmptyState
          icon={<MessageSquareWarning size={22} />}
          title={t('operations.noRecords')}
        />
      </Card>
    );
  return (
    <div className={styles.stack}>
      <RegisterCard
        kind={kind}
        icon={<MessageSquareWarning size={22} />}
        tiles
      />
    </div>
  );
}
